import { COLORS } from '@constants/colors';

// Design tokens for the Report It modal (MV-1077 redesign). The modal is a dark,
// translucent card over a blurred backdrop; fields are subtle translucent fills
// with pill-shaped action buttons (white "Cancel", red "Submit report").
const MODAL_BG = 'rgba(18, 18, 20, 0.92)';
const FIELD_BG = 'rgba(255, 255, 255, 0.05)';
const FIELD_BG_HOVER = 'rgba(255, 255, 255, 0.08)';
const FIELD_BORDER = 'rgba(255, 255, 255, 0.12)';
const PLACEHOLDER = 'rgba(255, 255, 255, 0.5)';
const SUBMIT_RED = '#E5242B';
const SUBMIT_RED_HOVER = '#C81E24';
const CANCEL_TEXT = '#111111';
const RADIUS_FIELD = '10px';
const RADIUS_PILL = '999px';

export const useStyles = () => ({
  backdrop: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    backdropFilter: 'blur(8px)',
  },
  dialogPaper: {
    width: 720,
    maxWidth: '92vw',
    bgcolor: MODAL_BG,
    backgroundImage: 'none',
    color: COLORS.WHITE,
    border: `1px solid ${FIELD_BORDER}`,
    borderRadius: '20px',
    boxShadow: '0 24px 80px rgba(0, 0, 0, 0.55)',
    backdropFilter: 'blur(20px)',
  },
  form: {
    position: 'relative',
  },
  formDragging: {
    position: 'relative',
    outline: `1px dashed ${PLACEHOLDER}`,
    outlineOffset: '-6px',
    borderRadius: '20px',
  },
  title: {
    fontSize: 20,
    fontWeight: 700,
    color: COLORS.WHITE,
    px: 4,
    pt: 3.5,
    pb: 1,
    pr: 7,
  },
  closeButton: {
    position: 'absolute',
    top: 18,
    right: 18,
    width: 32,
    height: 32,
    color: COLORS.WHITE_6,
    border: `1px solid ${FIELD_BORDER}`,
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: FIELD_BG,
    },
  },
  dialogContent: {
    display: 'grid',
    gap: 2,
    px: 4,
    pt: 1,
    pb: 1,
  },
  topRow: {
    display: 'flex',
    gap: 1.5,
    alignItems: 'stretch',
    flexDirection: { xs: 'column', sm: 'row' },
  },
  selectField: {
    flex: 1,
    minWidth: 0,
    '& .MuiInputBase-root': {
      color: COLORS.WHITE,
      backgroundColor: FIELD_BG,
      borderRadius: RADIUS_FIELD,
      height: 52,
    },
    '& .MuiSelect-select': {
      display: 'flex',
      alignItems: 'center',
      pr: '46px !important',
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.WHITE_6,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.WHITE_6,
    },
    // The chevron-in-a-circle must be styled from here, not from an `sx` on the
    // IconComponent itself: MUI renders it as `styled('svg')(...)` with `as={Icon}`
    // and hands its own emotion class down as `className`. Emotion composes that
    // class *after* the component's own `sx`, so MUI's `nativeSelectIconStyles` win
    // every shared property — which is how the arrow ended up black
    // (`palette.action.active` of the light default theme) and 3px low (`top:
    // calc(50% - .5em)` assumes a 1em-tall svg, ours is 22px). A descendant
    // selector outranks it on specificity instead of racing it on order.
    '& .MuiSelect-icon': {
      right: 14,
      top: 'calc(50% - 11px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 22,
      height: 22,
      borderRadius: '50%',
      border: `1px solid ${COLORS.WHITE_6}`,
      color: COLORS.WHITE_6,
    },
  },
  selectPlaceholder: {
    color: PLACEHOLDER,
  },
  attachField: {
    flexShrink: 0,
    width: { xs: '100%', sm: 200 },
    height: 52,
    px: 2,
    justifyContent: 'space-between',
    textTransform: 'none',
    fontWeight: 400,
    fontSize: 15,
    color: PLACEHOLDER,
    backgroundColor: FIELD_BG,
    border: `1px solid ${FIELD_BORDER}`,
    borderRadius: RADIUS_FIELD,
    '&:hover': {
      backgroundColor: FIELD_BG_HOVER,
      borderColor: COLORS.WHITE_6,
    },
    '& .MuiButton-endIcon': { color: COLORS.WHITE_6, ml: 1 },
    '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)' },
  },
  attachLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  textArea: {
    '& .MuiInputBase-root': {
      color: COLORS.WHITE,
      backgroundColor: FIELD_BG,
      borderRadius: RADIUS_FIELD,
      alignItems: 'flex-start',
      p: 2,
    },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
    '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.WHITE_6,
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: COLORS.WHITE_6,
    },
    '& textarea::placeholder': {
      color: PLACEHOLDER,
      opacity: 1,
      fontStyle: 'italic',
    },
  },
  actions: {
    justifyContent: 'center',
    gap: 2,
    px: 4,
    pb: 3.5,
    pt: 2,
  },
  cancelButton: {
    minWidth: 150,
    height: 48,
    borderRadius: RADIUS_PILL,
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 600,
    color: CANCEL_TEXT,
    backgroundColor: COLORS.WHITE,
    '&:hover': { backgroundColor: '#E6E6E6' },
  },
  submitButton: {
    minWidth: 170,
    height: 48,
    borderRadius: RADIUS_PILL,
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 600,
    color: COLORS.WHITE,
    backgroundColor: SUBMIT_RED,
    boxShadow: 'none',
    '&:hover': { backgroundColor: SUBMIT_RED_HOVER, boxShadow: 'none' },
    '&.Mui-disabled': {
      backgroundColor: 'rgba(229, 36, 43, 0.4)',
      color: 'rgba(255, 255, 255, 0.6)',
    },
  },
  menuPaper: {
    bgcolor: COLORS.NEW_BLACK,
    color: COLORS.WHITE,
    border: `1px solid ${FIELD_BORDER}`,
    borderRadius: RADIUS_FIELD,
    '& .MuiMenuItem-root:hover': { backgroundColor: FIELD_BG },
    '& .Mui-selected': { backgroundColor: FIELD_BG },
    '& .Mui-selected:hover': { backgroundColor: FIELD_BG_HOVER },
  },
  // Thank-you (success) view — centered, serif heading, keeps the report-id copy.
  successContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    minHeight: 240,
    px: 5,
    py: 5,
    gap: 1.5,
  },
  thankYouHeading: {
    fontFamily: '"Georgia", "Times New Roman", serif',
    fontSize: 44,
    fontWeight: 700,
    lineHeight: 1.1,
    color: COLORS.WHITE,
  },
  successAlert: {
    backgroundColor: 'transparent',
    color: COLORS.WHITE,
    fontSize: 15,
    p: 0,
    justifyContent: 'center',
    '& .MuiAlert-message': {
      overflowWrap: 'anywhere',
      textAlign: 'center',
      width: '100%',
    },
  },
  successDetail: {
    color: COLORS.WHITE_6,
    textAlign: 'center',
    maxWidth: 440,
  },
  attachmentWarning: {
    mt: 1,
    width: '100%',
  },
  attachmentList: {
    display: 'grid',
    gap: 0.75,
  },
  attachmentItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 1,
    backgroundColor: FIELD_BG,
    borderRadius: RADIUS_FIELD,
    px: 1.5,
    py: 0.75,
  },
  attachmentThumbnail: {
    width: 36,
    height: 36,
    flexShrink: 0,
    borderRadius: 1,
    objectFit: 'cover',
    border: `1px solid ${FIELD_BORDER}`,
  },
  // Stand-in for a thumbnail: a recording or log file must never be rendered
  // through an <img>, so its row shows a type icon instead.
  attachmentFileIcon: {
    width: 36,
    height: 36,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 1,
    border: `1px solid ${FIELD_BORDER}`,
    color: COLORS.WHITE_6,
  },
  attachmentInfo: {
    flex: 1,
    minWidth: 0,
  },
  attachmentMeta: {
    display: 'block',
    color: COLORS.WHITE_6,
    fontSize: 12,
  },
  iconButton: {
    color: COLORS.WHITE_6,
    '&:hover': { color: COLORS.WHITE },
  },
  uploadProgress: {
    mt: 0.5,
    height: 4,
    borderRadius: 2,
  },
});
